import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import styles from "./ContractorContacts.module.css";
import contractorContactsService from "../services/contractorContactsService";

const ContractorContacts = () => {
  const navigate = useNavigate();
  const [contractorContacts, setContractorContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCalendar, setSelectedCalendar] = useState("");
  const [pageSize, setPageSize] = useState(30);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [contactsForCalendar, setContactsForCalendar] = useState({});
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });

  useEffect(() => {
    loadContractorContacts();
  }, []);

  useEffect(() => {
    filterContacts();
  }, [contractorContacts, searchTerm, currentPage, pageSize, sortConfig]);

  const loadContractorContacts = async () => {
    try {
      setLoading(true);

      // Call backend API with DataTables parameters
      const params = {
        draw: 1,
        start: 0,
        length: 1000, // Get all records for local filtering
        search: { value: "" },
      };

      const data = await contractorContactsService.getAll(params);

      if (data.data && Array.isArray(data.data)) {
        // Transform backend data to match frontend expectations
        // Backend now returns clean JSON (no HTML) - frontend handles rendering
        const transformedContacts = data.data.map((contact) => {
          // Ensure we have a valid contactId - this is the key field for navigation
          const contactId = contact.contactId || contact.id;

          if (!contactId) {
            console.warn("Contact missing ID:", contact);
          }

          return {
            id: contactId,
            contactId: contactId, // This is the key field for calendar navigation
            name: contact.name || contact.full_name || "", // Use name or full_name - Laravel shows names
            full_name: contact.full_name || contact.name || "", // Add full_name for compatibility
            company: contact.company || "", // Clean company name - frontend renders link
            email: contact.email || "", // Clean email - frontend renders mailto link
            userEmail: contact.email || "", // Alias for email
            phone: contact.phone || "",
            userPhoneNumber: contact.phone || "",
            department: contact.department || "",
            job_title: contact.job_title || "",
            color: contact.color || "", // Clean color value - frontend renders color swatch
            contractor_id: contact.contractorId || contact.contractor_id,
            contractorId: contact.contractorId || contact.contractor_id,
            userId: contact.userId || null, // Ensure userId is set (can be null)
          };
        });

        // Filter out contacts without userId (can't be edited) and invalid contacts
        // Only show contacts that have both contactId and userId (required for editing)
        const validContacts = transformedContacts.filter(
          (contact) => 
            (contact.contactId || contact.contractorId) && 
            contact.userId // Must have userId to be editable
        );

        if (validContacts.length !== transformedContacts.length) {
          console.warn(
            `Filtered out ${
              transformedContacts.length - validContacts.length
            } contacts without valid IDs`
          );
        }

        setContractorContacts(validContacts);
        setTotalRecords(data.recordsTotal || validContacts.length);
        // Ensure contactsForCalendar is an object with {contactId: "Full Name"} format
        const calendarData = data.contactsForCalendar || {};
        setContactsForCalendar(typeof calendarData === 'object' && !Array.isArray(calendarData) ? calendarData : {});
        console.log(
          `Loaded ${validContacts.length} valid contractor contacts from backend`
        );
      } else {
        throw new Error("Invalid response format");
      }
    } catch (error) {
      console.error("Error loading contractor contacts:", error);
      // No mock data fallback - show empty state
      setContractorContacts([]);
      setTotalRecords(0);
      setContactsForCalendar({});
      toast.error("Failed to load contractor contacts. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const filterContacts = () => {
    let filtered = [...contractorContacts];

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(
        (contact) =>
          contact.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          contact.company.toLowerCase().includes(searchTerm.toLowerCase()) ||
          contact.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
          contact.job_title.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply calendar filter
    if (selectedCalendar) {
      filtered = filtered.filter(
        (contact) => contact.company === selectedCalendar
      );
    }

    // Apply sorting
    if (sortConfig.key) {
      filtered.sort((a, b) => {
        const aValue = a[sortConfig.key] || "";
        const bValue = b[sortConfig.key] || "";

        if (aValue < bValue) {
          return sortConfig.direction === "asc" ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === "asc" ? 1 : -1;
        }
        return 0;
      });
    }

    setFilteredContacts(filtered);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handlePageSizeChange = (e) => {
    setPageSize(parseInt(e.target.value));
    setCurrentPage(1);
  };

  const handleCalendarChange = (e) => {
    const contactId = e.target.value;
    setSelectedCalendar(contactId);
    if (contactId) {
      // Navigate to the contractor contact calendar
      navigate(`/contractors/contacts/${contactId}/calendar`);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleCalendarClick = (contactId) => {
    console.log("Calendar clicked for contact:", contactId, typeof contactId);

    if (!contactId || contactId === "undefined" || contactId === undefined) {
      console.error("Invalid contactId for calendar navigation:", contactId);
      toast.error("Cannot open calendar: Invalid contact ID");
      return;
    }

    console.log("Navigating to calendar for contact ID:", contactId);
    navigate(`/contractors/contacts/${contactId}/calendar`);
  };

  const handleEditClick = (contactId, userId) => {
    // Laravel uses: route('users.edit', $model->userId)
    // So we need userId directly from the contact data
    
    // Store the return URL and contractor_id in localStorage so we can return here after editing
    localStorage.setItem("returnUrl", "/contractors/contacts");

    // Find the contact to get contractor_id and ensure we have the correct userId
    const contact = contractorContacts.find((c) => c.contactId === contactId);
    if (!contact) {
      toast.error("Contact not found. Cannot edit.");
      return;
    }

    if (contact && contact.contractor_id) {
      localStorage.setItem("contractor_id", contact.contractor_id);
    }

    // Use userId from contact data (Laravel uses $model->userId directly)
    const actualUserId = contact.userId || userId;
    
    if (!actualUserId) {
      toast.error("User ID not available. Cannot edit this contact.");
      return;
    }

    // Navigate to user edit page with userId (matches Laravel: route('users.edit', $model->userId))
    navigate(`/users/${actualUserId}/edit`);
  };

  const handleRemoveClick = async (contactId, contactName) => {
    if (window.confirm(`Are you sure you want to remove ${contactName}?`)) {
      try {
        const response = await contractorContactsService.delete(contactId);
        if (response.success) {
          toast.success(`${contactName} has been removed successfully`);
          loadContractorContacts();
        } else {
          toast.error(response.message || "Failed to remove contact");
        }
      } catch (error) {
        console.error("Error removing contact:", error);
        // Check if error is about related tickets
        if (error.message && error.message.includes("related tickets")) {
          toast.error(error.message);
        } else {
          toast.error(error.message || "Failed to remove contact");
        }
      }
    }
  };

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return "⇅";
    }
    return sortConfig.direction === "asc" ? "↑" : "↓";
  };

  const getUniqueCompanies = () => {
    const companies = [
      ...new Set(contractorContacts.map((contact) => contact.company)),
    ];
    return companies.filter((company) => company);
  };

  const getPaginatedContacts = () => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredContacts.slice(startIndex, endIndex);
  };

  const totalPages = Math.ceil(filteredContacts.length / pageSize);
  const startRecord = (currentPage - 1) * pageSize + 1;
  const endRecord = Math.min(currentPage * pageSize, filteredContacts.length);

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingSpinner}>
          <div className={styles.spinner}></div>
          <p>Loading contractor contacts...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Breadcrumb */}
      <div className={styles.breadcrumb}>
        <ol>
          <li>
            <a href="/dashboard">Dashboard</a>
          </li>
          <li>
            <a href="/contractors">Contractors</a>
          </li>
          <li className={styles.active}>Contacts</li>
        </ol>
      </div>

      {/* Calendar Selection */}
      <div className={styles.calendarSection}>
        <label htmlFor="calendarSelect">
          Go to contractor contact calendar:
        </label>
        <select
          id="calendarSelect"
          value={selectedCalendar}
          onChange={handleCalendarChange}
          className={styles.calendarSelect}
        >
          <option value="">Please select</option>
          {Object.entries(contactsForCalendar).map(
            ([contactId, contactName]) => (
              <option key={contactId} value={contactId}>
                {contactName}
              </option>
            )
          )}
        </select>
      </div>

      {/* Main Content */}
      <div className={styles.contentArea}>
        <h1 className={styles.pageTitle}>Contractor Contacts</h1>

        {/* Table Controls */}
        <div className={styles.tableControls}>
          <div className={styles.entriesControl}>
            <label htmlFor="pageSize">Show</label>
            <select
              id="pageSize"
              value={pageSize}
              onChange={handlePageSizeChange}
              className={styles.entriesSelect}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={30}>30</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <span>entries</span>
          </div>

          <div className={styles.tableSearch}>
            <label htmlFor="searchInput">Search:</label>
            <input
              id="searchInput"
              type="text"
              value={searchTerm}
              onChange={handleSearchChange}
              placeholder=""
              className={styles.searchInput}
            />
          </div>
        </div>

        {/* Table */}
        <div className={styles.tableWrapper}>
          <table className={styles.contractorContactsTable}>
            <thead>
              <tr>
                <th onClick={() => handleSort("name")} style={{ cursor: "pointer" }}>
                  Name
                  <span className={styles.sortIcon}>{getSortIcon("name")}</span>
                </th>
                <th onClick={() => handleSort("company")} style={{ cursor: "pointer" }}>
                  Company
                  <span className={styles.sortIcon}>{getSortIcon("company")}</span>
                </th>
                <th onClick={() => handleSort("email")} style={{ cursor: "pointer" }}>
                  E-mail
                  <span className={styles.sortIcon}>{getSortIcon("email")}</span>
                </th>
                <th onClick={() => handleSort("phone")} style={{ cursor: "pointer" }}>
                  Phone
                  <span className={styles.sortIcon}>{getSortIcon("phone")}</span>
                </th>
                <th onClick={() => handleSort("department")} style={{ cursor: "pointer" }}>
                  Department
                  <span className={styles.sortIcon}>{getSortIcon("department")}</span>
                </th>
                <th onClick={() => handleSort("job_title")} style={{ cursor: "pointer" }}>
                  Job title
                  <span className={styles.sortIcon}>{getSortIcon("job_title")}</span>
                </th>
                <th onClick={() => handleSort("color")} style={{ cursor: "pointer" }}>
                  Calendar color
                  <span className={styles.sortIcon}>{getSortIcon("color")}</span>
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {getPaginatedContacts().map((contact) => (
                <tr key={contact.contactId}>
                  <td>{contact.name}</td>
                  <td>
                    <a
                      href={`/contractors/${contact.contractorId}`}
                      className={styles.companyLink}
                    >
                      {contact.company}
                    </a>
                  </td>
                  <td>
                    <a
                      href={`mailto:${contact.userEmail || contact.email}`}
                      className={styles.emailLink}
                    >
                      {contact.userEmail || contact.email}
                    </a>
                  </td>
                  <td>{contact.userPhoneNumber || contact.phone || ""}</td>
                  <td>{contact.department || ""}</td>
                  <td>{contact.job_title || ""}</td>
                  <td>
                    {contact.color && (
                      <span
                        className={styles.colorSwatch}
                        style={{ backgroundColor: contact.color }}
                      >
                        {contact.color}
                      </span>
                    )}
                  </td>
                  <td className={styles.actionsCell}>
                    <button
                      onClick={() => handleCalendarClick(contact.contactId)}
                      className={`${styles.actionButton} ${styles.calendarButton}`}
                    >
                      Calendar
                    </button>
                    <button
                      onClick={() =>
                        handleEditClick(contact.contactId, contact.userId)
                      }
                      className={`${styles.actionButton} ${styles.editButton}`}
                      disabled={!contact.userId} // Disable if no userId
                      title={!contact.userId ? "User not available for editing" : "Edit user"}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() =>
                        handleRemoveClick(contact.contactId, contact.name)
                      }
                      className={`${styles.actionButton} ${styles.removeButton}`}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className={styles.paginationWrapper}>
          <div className={styles.paginationInfo}>
            Showing {startRecord} to {endRecord} of {filteredContacts.length}{" "}
            entries
          </div>

          <div className={styles.pagination}>
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className={`${styles.paginationButton} ${
                currentPage === 1 ? styles.disabled : ""
              }`}
            >
              Previous
            </button>

            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }

              return (
                <button
                  key={pageNum}
                  onClick={() => handlePageChange(pageNum)}
                  className={`${styles.paginationButton} ${
                    currentPage === pageNum ? styles.active : ""
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}

            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className={`${styles.paginationButton} ${
                currentPage === totalPages ? styles.disabled : ""
              }`}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContractorContacts;
